import { ref, computed, onMounted, onUnmounted, readonly } from 'vue'

/**
 * Device detection composable
 * Provides reactive device type detection and screen size information
 * Shared between desktop and mobile components for adaptive behavior
 */
export const useDeviceDetection = () => {
  // Reactive state
  const screenWidth = ref(typeof window !== 'undefined' ? window.innerWidth : 0)
  const screenHeight = ref(typeof window !== 'undefined' ? window.innerHeight : 0)
  const orientation = ref(
    typeof window !== 'undefined' && window.innerHeight > window.innerWidth ? 'portrait' : 'landscape',
  )
  
  // Device type detection based on screen width
  const isMobile = computed(() => screenWidth.value < 768)
  const isTablet = computed(() => screenWidth.value >= 768 && screenWidth.value < 1024)
  const isDesktop = computed(() => screenWidth.value >= 1024)
  
  // Touch device detection
  const isTouchDevice = computed(() => {
    return 'ontouchstart' in window || navigator.maxTouchPoints > 0
  })
  
  // Device orientation
  const isPortrait = computed(() => orientation.value === 'portrait')
  const isLandscape = computed(() => orientation.value === 'landscape')
  
  // Breakpoint helpers
  const breakpoints = {
    xs: 0,
    sm: 640,
    md: 768,
    lg: 1024,
    xl: 1280,
    '2xl': 1536
  }
  
  const isBreakpoint = (breakpoint) => {
    return computed(() => screenWidth.value >= breakpoints[breakpoint])
  }
  
  // Update device information
  const updateDeviceInfo = () => {
    screenWidth.value = window.innerWidth
    screenHeight.value = window.innerHeight
    orientation.value = window.innerHeight > window.innerWidth ? 'portrait' : 'landscape'
  }
  
  // Handle orientation change
  const handleOrientationChange = () => {
    // Small delay to ensure dimensions are updated
    setTimeout(updateDeviceInfo, 100)
  }
  
  // Lifecycle hooks
  onMounted(() => {
    updateDeviceInfo()
    window.addEventListener('resize', updateDeviceInfo)
    window.addEventListener('orientationchange', handleOrientationChange)
  })
  
  onUnmounted(() => {
    window.removeEventListener('resize', updateDeviceInfo)
    window.removeEventListener('orientationchange', handleOrientationChange)
  })
  
  return {
    // Reactive properties
    screenWidth: readonly(screenWidth),
    screenHeight: readonly(screenHeight),
    orientation: readonly(orientation),
    
    // Device type detection
    isMobile: readonly(isMobile),
    isTablet: readonly(isTablet),
    isDesktop: readonly(isDesktop),
    isTouchDevice: readonly(isTouchDevice),
    
    // Orientation helpers
    isPortrait: readonly(isPortrait),
    isLandscape: readonly(isLandscape),
    
    // Breakpoint helpers
    isBreakpoint,
    breakpoints,
    
    // Methods
    updateDeviceInfo
  }
}